'use client';

import { SheetPickerList } from '@/components/global/SheetPickerList';
import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalFooter,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
} from '@/components/ui/responsive-modal';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { useTranslations } from '@/hooks/useTranslations';
import axios from '@/lib/axios';
import { router } from '@inertiajs/react';
import { Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

interface ImpersonatableUser {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    avatar?: string;
    role?: string | null;
}

interface UserImpersonationModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export function UserImpersonationModal({ open, onOpenChange }: UserImpersonationModalProps) {
    const { t } = useTranslations();
    const [users, setUsers] = useState<ImpersonatableUser[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
    const [starting, setStarting] = useState(false);

    useEffect(() => {
        if (!open) {
            setSelectedUserId(null);
        }
    }, [open]);

    // The endpoint returns every active user, so the list is fetched once per
    // opening and the picker filters it — no round trip per keystroke.
    useEffect(() => {
        if (!open) {
            return;
        }

        let cancelled = false;

        void (async () => {
            setLoading(true);
            try {
                const response = await axios.get('/api/impersonate/users/search');
                if (cancelled) {
                    return;
                }
                setUsers(Array.isArray(response.data?.users) ? response.data.users : []);
            } catch {
                if (!cancelled) {
                    setUsers([]);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [open]);

    /**
     * One heading per role so an admin can find the account they need by the
     * permissions they are after; the roleless sit at the end. Names inside a
     * group keep the order the endpoint sorted them into.
     */
    const options = useMemo(() => {
        const noRoleHeading = t('common.impersonation.no_role', 'No role');

        return [...users]
            .map((user) => ({
                user,
                group: user.role?.trim() ? user.role.trim() : noRoleHeading,
            }))
            .sort((a, b) => {
                if (a.group === b.group) {
                    return 0;
                }
                // The roleless bucket is a fallback, not a role — it goes last.
                if (a.group === noRoleHeading) {
                    return 1;
                }
                if (b.group === noRoleHeading) {
                    return -1;
                }

                return a.group.localeCompare(b.group);
            })
            .map(({ user, group }) => ({
                id: user.id,
                label: [user.first_name, user.last_name].filter(Boolean).join(' ').trim() || user.email,
                description: user.email,
                keywords: user.role ?? undefined,
                badge: user.role ? <Badge variant="secondary">{user.role}</Badge> : null,
                group,
            }));
    }, [users, t]);

    const handleImitate = () => {
        if (!selectedUserId) {
            return;
        }

        setStarting(true);
        router.post(
            `/impersonate/${selectedUserId}`,
            {},
            {
                onSuccess: () => {
                    onOpenChange(false);
                    setSelectedUserId(null);
                },
                onFinish: () => setStarting(false),
            },
        );
    };

    return (
        <ResponsiveModal open={open} onOpenChange={onOpenChange} variant="sheet">
            <ResponsiveModalContent size="md">
                <ResponsiveModalHeader>
                    <ResponsiveModalTitle>{t('common.impersonation.title', 'Imitate User')}</ResponsiveModalTitle>
                    <ResponsiveModalDescription>
                        {t('common.impersonation.description', 'Select a user to imitate. You will see the application with their permissions.')}
                    </ResponsiveModalDescription>
                </ResponsiveModalHeader>

                <ResponsiveModalBody>
                    {/* Listed in the sheet rather than behind a combobox, like the
                        assignment sheets. */}
                    <SheetPickerList
                        value={selectedUserId}
                        onValueChange={setSelectedUserId}
                        options={options}
                        loading={loading && options.length === 0}
                        labels={{
                            search: t('common.impersonation.search', 'Search users by name or email…'),
                            empty: t('common.impersonation.empty', 'No users found.'),
                        }}
                    />
                </ResponsiveModalBody>

                <ResponsiveModalFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={starting}>
                        {t('common.cancel', 'Cancel')}
                    </Button>
                    <Button onClick={handleImitate} disabled={!selectedUserId || starting}>
                        {starting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {t('common.impersonation.confirm', 'Imitate')}
                    </Button>
                </ResponsiveModalFooter>
            </ResponsiveModalContent>
        </ResponsiveModal>
    );
}
